/**
 * Security utility functions for client-side security
 */

/**
 * Securely stores the authentication token
 * @param {string} token - The authentication token to store
 */
export const storeAuthToken = (token) => {
  if (!token) return;
  
  try {
    // Store token in localStorage with expiration
    const tokenData = {
      value: token,
      expires: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7 days
    };
    localStorage.setItem('authToken', JSON.stringify(tokenData));
    localStorage.setItem('token', token);
  } catch (error) {
    console.error('Error storing auth token:', error);
  }
};

/**
 * Retrieves the stored authentication token if valid and not expired
 * @returns {string|null} - The authentication token or null if expired/invalid
 */
export const getAuthToken = () => {
  try {
    const tokenData = JSON.parse(localStorage.getItem('authToken'));
    
    if (!tokenData) return null;
    
    // Check if token is expired based on our storage expiration
    if (tokenData.expires && tokenData.expires < Date.now()) {
      // Token expired, remove it
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      return null;
    }
    
    // Also validate the JWT token itself
    if (!isTokenValid(tokenData.value)) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      return null;
    }
    
    return tokenData.value;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

/**
 * Removes the stored authentication token
 */
export const removeAuthToken = () => {
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
  } catch (error) {
    console.error('Error removing auth token:', error);
  }
};

/**
 * Securely stores user data
 * @param {Object} userData - The user data to store
 */
export const storeUserData = (userData) => {
  if (!userData) return;
  
  try {
    // Remove sensitive fields before storing
    const { password, resetPasswordToken, emailVerificationToken, ...safeUserData } = userData;
    
    localStorage.setItem('userData', JSON.stringify(safeUserData));
  } catch (error) {
    console.error('Error storing user data:', error);
  }
};

/**
 * Retrieves the stored user data
 * @returns {Object|null} - The user data or null if not found/invalid
 */
export const getUserData = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData'));
    return userData || null;
  } catch (error) {
    console.error('Error retrieving user data:', error);
    return null;
  }
};

/**
 * Removes the stored user data
 */
export const removeUserData = () => {
  try {
    localStorage.removeItem('userData');
  } catch (error) {
    console.error('Error removing user data:', error);
  }
};

/**
 * Clears all authentication-related data
 */
export const clearAuthData = () => {
  removeAuthToken();
  removeUserData();
};

/**
 * Checks if a token is valid (not expired and properly formatted)
 * @param {string} token - The token to validate
 * @returns {boolean} - Whether the token is valid
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    // Basic JWT structure validation
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Check expiration if possible
    const payload = JSON.parse(atob(parts[1]));
    
    // Log when token is expired for debugging
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};